import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Check, ChevronLeft, Loader2, Clock, ShieldCheck } from "lucide-react";

const TIMES = ["8:00 AM", "9:00 AM", "10:00 AM", "1:00 PM", "2:00 PM", "3:00 PM"];
const STEPS = ["Service", "Block", "Date & Time", "Details", "Deposit"];

export const BookingModal = ({ open, onClose, presetServiceId }) => {
  const [services, setServices] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [availability, setAvailability] = useState({ open_weekdays: [1,2,3,4,5,6], blocked_dates: [] });
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service_id: "", block_id: "", date: "", time: "",
    name: "", phone: "", email: "", address: "", city: "Pensacola", access_notes: "", referral_source: "",
  });

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/services"), api.get("/blocks"), api.get("/availability")]).then(([s, b, a]) => {
      setServices(s.data.filter((x) => x.bookable));
      setBlocks(b.data);
      setAvailability(a.data);
    });
    setStep(0);
    setForm((f) => ({ ...f, service_id: presetServiceId || "" }));
    if (presetServiceId) setStep(1);
  }, [open, presetServiceId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedService = services.find((s) => s.id === form.service_id);
  const selectedBlock = blocks.find((b) => b.id === form.block_id);

  const dateValid = (d) => {
    if (!d) return false;
    const dt = new Date(d + "T00:00:00");
    if (dt < new Date(new Date().toDateString())) return false;
    if (!availability.open_weekdays.includes(dt.getDay())) return false;
    if (availability.blocked_dates.includes(d)) return false;
    return true;
  };

  const canNext = () => {
    if (step === 0) return !!form.service_id;
    if (step === 1) return !!form.block_id;
    if (step === 2) return dateValid(form.date) && !!form.time;
    if (step === 3) return form.name && form.phone && /\S+@\S+\.\S+/.test(form.email) && form.address;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", form);
      const { data: checkout } = await api.post("/payments/checkout/booking", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start booking");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="booking-modal" className="max-w-lg p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 bg-brand-ceruleanDark text-white">
          <DialogTitle className="font-serif text-2xl">Book a Service Block</DialogTitle>
          <DialogDescription className="sr-only">Multi-step booking: choose a service, block, date and time, enter details, and pay the deposit.</DialogDescription>
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-brand-gold" : "bg-white/25"}`} />
                <span className={`mt-1.5 block text-[10px] ${i === step ? "text-brand-gold" : "text-white/50"}`}>{s}</span>
              </div>
            ))}
          </div>
        </DialogHeader>

        <div className="p-6 overflow-y-auto">
          {step === 0 && (
            <div data-testid="booking-step-service-select" className="space-y-3">
              <p className="text-sm text-brand-slate">Choose an instantly-bookable service. Staging & rearrangement go through a free consultation.</p>
              {services.map((s) => (
                <button key={s.id} data-testid={`booking-service-${s.id}`} onClick={() => set("service_id", s.id)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.service_id === s.id ? "border-brand-cerulean bg-brand-ceruleanLight" : "border-brand-border hover:border-brand-gold"}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <div className="font-semibold text-brand-navy">{s.name}</div>
                      <div className="text-xs text-brand-slate mt-0.5">{s.tagline}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-brand-cerulean">${s.deposit} dep.</div>
                      <div className="text-[11px] text-brand-slate">{s.starting_price}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {blocks.map((b) => (
                <button key={b.id} data-testid={`booking-block-${b.id}`} onClick={() => set("block_id", b.id)}
                  className={`w-full text-left p-4 rounded-xl border-2 flex items-center justify-between transition-all ${form.block_id === b.id ? "border-brand-cerulean bg-brand-ceruleanLight" : "border-brand-border hover:border-brand-gold"}`}>
                  <div>
                    <div className="font-semibold text-brand-navy">{b.name}</div>
                    <div className="text-xs text-brand-slate flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {b.window}</div>
                  </div>
                  {form.block_id === b.id && <Check className="w-5 h-5 text-brand-cerulean" />}
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div data-testid="booking-step-time-select" className="space-y-4">
              <div>
                <Label className="text-brand-navy">Select date</Label>
                <Input type="date" data-testid="booking-date-input" value={form.date}
                       min={new Date().toISOString().split("T")[0]}
                       onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
                {form.date && !dateValid(form.date) && (
                  <p className="text-xs text-brand-coral mt-1.5">That day is closed or blocked. Please choose another date.</p>
                )}
              </div>
              <div>
                <Label className="text-brand-navy">Preferred start time</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {TIMES.map((t) => (
                    <button key={t} data-testid={`booking-time-${t.replace(/[: ]/g,'')}`} onClick={() => set("time", t)}
                      className={`py-2 rounded-lg text-sm border-2 transition-all ${form.time === t ? "border-brand-cerulean bg-brand-ceruleanLight text-brand-cerulean font-semibold" : "border-brand-border text-brand-slate hover:border-brand-gold"}`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="booking-step-customer-info" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full name</Label><Input data-testid="booking-name-input" value={form.name} onChange={(e)=>set("name",e.target.value)} className="mt-1" /></div>
                <div><Label>Phone</Label><Input data-testid="booking-phone-input" value={form.phone} onChange={(e)=>set("phone",e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label>Email</Label><Input data-testid="booking-email-input" type="email" value={form.email} onChange={(e)=>set("email",e.target.value)} className="mt-1" /></div>
              <div><Label>Property address</Label><Input data-testid="booking-address-input" value={form.address} onChange={(e)=>set("address",e.target.value)} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>City</Label><Input data-testid="booking-city-input" value={form.city} onChange={(e)=>set("city",e.target.value)} className="mt-1" /></div>
                <div><Label>Realtor referral (optional)</Label><Input data-testid="booking-referral-input" value={form.referral_source} onChange={(e)=>set("referral_source",e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label>Access notes (optional)</Label><Textarea data-testid="booking-notes-input" value={form.access_notes} onChange={(e)=>set("access_notes",e.target.value)} className="mt-1" rows={2} /></div>
            </div>
          )}

          {step === 4 && selectedService && (
            <div className="space-y-4">
              <div className="rounded-xl border border-brand-border p-4 bg-brand-goldSoft/50">
                <div className="flex justify-between text-sm py-1"><span className="text-brand-slate">Service</span><span className="font-medium text-brand-navy">{selectedService.name}</span></div>
                <div className="flex justify-between text-sm py-1"><span className="text-brand-slate">Block</span><span className="font-medium text-brand-navy">{selectedBlock?.name}</span></div>
                <div className="flex justify-between text-sm py-1"><span className="text-brand-slate">When</span><span className="font-medium text-brand-navy">{form.date} · {form.time}</span></div>
                <div className="border-t border-brand-border my-2" />
                <div className="flex justify-between text-base py-1"><span className="text-brand-navy font-semibold">Deposit due now</span><span className="font-bold text-brand-cerulean">${selectedService.deposit}.00</span></div>
                <p className="text-xs text-brand-slate mt-1">Remaining balance is sent as a secure Stripe payment link before your appointment.</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-brand-emerald"><ShieldCheck className="w-4 h-4" /> Secure Stripe checkout · Test card 4242 4242 4242 4242</div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-brand-border flex items-center justify-between gap-3 bg-white">
          {step > 0 ? (
            <Button variant="ghost" data-testid="booking-back-btn" onClick={() => setStep(step - 1)} className="text-brand-slate">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : <span />}
          {step < 4 ? (
            <Button data-testid="booking-next-btn" disabled={!canNext()} onClick={() => setStep(step + 1)}
                    className="bg-brand-cerulean hover:bg-brand-ceruleanDark text-white rounded-full px-8">Continue</Button>
          ) : (
            <Button data-testid="booking-submit-deposit-btn" disabled={submitting} onClick={submit}
                    className="brand-gold-shine bg-brand-gold hover:bg-brand-goldHover text-white rounded-full px-8">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</> : `Pay $${selectedService?.deposit} Deposit`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
